import { Grid2, Typography } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Elements } from "@stripe/react-stripe-js";
import Breadcrumbs from "../../../../components/Breadcrumbs";
import { useTranslate } from "../../../../hooks/useTranslate";
import { useStudents } from "../../../../context/students";
import axiosManager from "../../../../services/axiosManager";
import OptionsToggle from "../../../../components/OptionsToggle";
import SummaryBox from "../../../../components/SummaryBox";
import PaymentSummary from "../../../applicant/TuitionBilling/components/PaymentSummary";
import Preloader from "../../../../components/Preloader";
import { formatCurrency } from "../../../../utils/formatCurrency";
import { isVersionUK } from "../../../../services/versionService";
import CheckoutForm from "../../../applicant/TuitionBilling/components/CheckoutForm";
import CheckoutStatus from "../../../applicant/TuitionBilling/components/CheckoutStatus";
import SnackbarToast from "../../../../components/SnackbarToast";
import Students1 from "../../../../assets/images/images/students3.jpg";
import Students2 from "../../../../assets/images/images/students4.jpg";
import useStatuses from "../../../../context/status/useStatuses";
import { stripeAppearance, stripeFonts, stripePromise } from "../../../../services/stripeConfig";

const currency = isVersionUK ? "GBP" : "USD";

export const RenewalPayment = () => {
    const { t } = useTranslate();
    const navigate = useNavigate();
    const { renewalStatus, refetchGeneralStatuses } = useStatuses();

    const [searchParams] = useSearchParams();
    const hasPaymentComplete = searchParams.has("payment-complete");
    const isPaymentProcessing = renewalStatus?.paymentStatus === "processing";

    useEffect(() => {
        if (isPaymentProcessing) refetchGeneralStatuses();
    }, [isPaymentProcessing]);

    const { activeStudent, activeStudentId } = useStudents();
    const studentFullName = `${activeStudent.forename} ${activeStudent.surname}`;

    const [paymentType, setPaymentType] = useState("Full");

    const { isLoading, data: paymentPlans, refetch } = useQuery({
        queryKey: [`student/${activeStudentId}/RenewalPaymentPlans`],
        queryFn: () => axiosManager.get$(`/parent/api/students/${activeStudentId}/RenewalPaymentPlans`).then((response) => response?.data),
        retry: 0,
        cacheTime: 0,
        keepPreviousData: true,
        enabled: !!activeStudentId,
    });

    const {
        isLoading: isLoadingRolling, data: isOnRollingContract,
    } = useQuery({
        queryKey: [`student/${activeStudentId}/contracts/is-on-rolling`],
        queryFn: () => axiosManager.get$(`/parent/api/contracts/${activeStudentId}`).then((response) => response?.data),
        retry: 0,
        cacheTime: 0,
        select: (data) => data.contractType === "Rolling",
        enabled: !!activeStudentId && !(hasPaymentComplete || isPaymentProcessing),
    });

    const {
        mutateAsync: setPaymentPlan,
        isPending: isSettingPaymentPlan,
        error: errorSetPaymentPlan,
        reset: resetPaymentPlan,
    } = useMutation({
        mutationFn: (planName) => axiosManager.patch$(`/parent/api/students/${activeStudentId}/RenewalPaymentPlans/${planName}`),
    });
    const {
        mutateAsync: configureEnrollmentPayments,
        isPending: isConfiguring,
        error: errorConfigure,
        reset: resetConfigure,
        data: payment,
    } = useMutation({
        mutationFn: () => axiosManager
            .post$(`/parent/api/Payment/${activeStudentId}/ConfigureRenewalPayments`)
            .then((response) => response?.data),
    });

    const { isLoading: isLoadingSubscription, data: subscription } = useQuery({
        queryKey: [`Payment/${activeStudentId}/Subscription`],
        queryFn: () => axiosManager.get$(`/parent/api/Payment/${activeStudentId}/Subscription`).then((response) => response?.data?.paymentConfiguration),
        retry: 0,
        cacheTime: 0,
        keepPreviousData: true,
        enabled: !!activeStudentId && !isLoading && paymentPlans?.length === 0,
    });

    const { isLoading: isLoadingSummary, data: summary } = useQuery({
        queryKey: [`Payment/${activeStudentId}/Summary`],
        queryFn: () => axiosManager.get$(`/parent/api/Payment/${activeStudentId}/Summary`).then((response) => response?.data),
        retry: 0,
        cacheTime: 0,
        keepPreviousData: true,
        enabled: !!activeStudentId && !isLoading && paymentPlans?.length === 0 && !isLoadingSubscription && !subscription,
    });

    const stripeOptions = useMemo(() => ({
        clientSecret: subscription?.clientSecret || payment?.clientSecret,
        appearance: stripeAppearance,
        fonts: stripeFonts,
        loader: "auto",
    }), [subscription?.clientSecret, payment?.clientSecret]);

    const paymentPlansOptions = useMemo(
        () => (paymentPlans || [])
            .filter((plan) => (isOnRollingContract ? plan.type !== "Monthly" : true))
            .map((plan) => ({
                value: plan.type,
                title:
                plan.type === "Monthly"
                    ? t("tuitionBilling.payMonthly")
                    : t(isOnRollingContract ? "tuitionBilling.payHalfTermly" : "tuitionBilling.payInFull"),
                onePaymentSum: plan.oneOffAmount,
                content: (
                    <>
                        {plan.oneOffAmount > 0 && (
                            <>
                                <b>{formatCurrency(currency).format(plan.oneOffAmount)}</b>
                                {` ${t(plan.instalments.length > 0 ? "tuitionBilling.forMonth" : "tuitionBilling.oneOffPayment", { num: plan.instalments.length })}${
                                    (plan.instalments.length > 12 && plan.deposit !== 0) ? ` + ${t("tuitionBilling.refundableDepositDue")} ` : ""
                                }`}
                            </>
                        )}
                        {(plan.instalments.length > 12 && plan.deposit !== 0) && (
                            <b>{formatCurrency(currency).format(plan.deposit)}</b>
                        )}
                    </>
                ),
            })),
        [paymentPlans, isOnRollingContract],
    );
    const paymentSelected = (paymentPlans || []).find(
        (option) => option.type === paymentType,
    );

    const onProceed = async () => {
        try {
            await setPaymentPlan(paymentType);
            await configureEnrollmentPayments();
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        if (activeStudentId) {
            resetConfigure();
            setPaymentType("Full");
        }
    }, [activeStudentId]);

    return (
        <div className="page-content">
            <div className="student-head-section" />
            <Grid2 container spacing={2} className={`mb-8 ${isPaymentProcessing ? "min-head-section-height" : ""}`}>
                <Grid2 size={{ xs: 12, md: 7, lg: 8 }} className="text-white">
                    <Breadcrumbs
                        breadcrumbs={[
                            { label: t("navigation.home"), href: `/home/${activeStudentId}` },
                            { label: t("renewals.title"), href: `/renewals/${activeStudentId}` },
                            { label: t("managePayments.title"), current: true },
                        ]}
                    />
                    <Typography variant="h1" className="text-white">
                        {t("managePayments.title")}
                    </Typography>
                    <Typography variant="body1" className="mt-4 text-white" component="div">
                        {t("managePayments.subtitle")}
                        <a
                            className="text-white"
                            href={`mailto:${t("finance.emailAccounts")}`}
                        >
                            {t("finance.emailAccounts")}
                        </a>
                    </Typography>
                </Grid2>
                <Grid2 size={{ xs: 12, md: 5, lg: 4 }}>
                    <div className="images-head-section">
                        <div className="img-head-section">
                            <img alt="students-1" src={Students1} />
                        </div>
                        <div className="img-head-section">
                            <img alt="students-2" src={Students2} />
                        </div>
                    </div>
                </Grid2>
            </Grid2>
            <div className="relative z-1">
                <Preloader isLoading={isLoading || isLoadingSubscription || isLoadingSummary}>
                    {hasPaymentComplete && (
                        <Elements options={stripeOptions} stripe={stripePromise}>
                            <CheckoutStatus />
                        </Elements>
                    )}
                    {isPaymentProcessing && (
                        <Elements options={stripeOptions} stripe={stripePromise}>
                            <CheckoutStatus isProcessing />
                        </Elements>
                    )}
                    {(!hasPaymentComplete && !isPaymentProcessing && (subscription || payment)) && (
                        <Elements
                            options={stripeOptions}
                            stripe={stripePromise}
                        >
                            <CheckoutForm
                                studentFullName={studentFullName}
                                selectedType={paymentType}
                                isOnRollingContract={isOnRollingContract}
                                invoice={subscription?.invoice || payment?.invoice}
                                instalment={subscription?.recurringAmount || payment?.recurringAmount || paymentSelected?.instalments?.[0]?.amount}
                            />
                        </Elements>
                    )}
                    {(!hasPaymentComplete && !isPaymentProcessing && !subscription && !payment && paymentPlans?.length > 0) && (
                        <Preloader isLoading={isLoadingRolling || isSettingPaymentPlan || isConfiguring} showInitiallyOverflow overflow>
                            <Typography variant="h2" className="text-white">
                                {t("managePayments.paymentSummary", { studentFullName })}
                            </Typography>
                            <OptionsToggle
                                label={studentFullName}
                                options={paymentPlansOptions}
                                value={paymentType}
                                onChange={setPaymentType}
                                className="mt-8 mb-12"
                            />
                            {paymentSelected && (
                                <SummaryBox
                                    className="mb-12"
                                    contentClassName="p-8"
                                    isFormBox
                                >
                                    <PaymentSummary
                                        deposit={paymentSelected.deposit}
                                        discounts={paymentSelected.discounts}
                                        subtext={
                                            paymentSelected.type === "Monthly"
                                                ? t("tuitionBilling.payMonthly")
                                                : t("tuitionBilling.payInFull")
                                        }
                                        onePaymentSum={
                                            paymentSelected.oneOffAmount
                                        }
                                        techFee={paymentSelected.techFee}
                                        total={paymentSelected.amountDue}
                                        studentFullName={studentFullName}
                                        onProceed={onProceed}
                                        reload={refetch}
                                    />
                                </SummaryBox>
                            )}
                        </Preloader>
                    )}
                    {(!hasPaymentComplete && !isPaymentProcessing && !subscription && !payment && paymentPlans?.length === 0) && (
                        <>
                            <Typography variant="h2" className="mt-20 text-white">
                                {t("managePayments.paymentSummary", { studentFullName })}
                            </Typography>
                            <Grid2 container spacing={2} className="mt-8">
                                <Grid2 size={{ xs: 12, md: 7 }}>
                                    <div className="details-box">
                                        <div className="details-box__row">
                                            <span className="details-box__title">{t("managePayments.studentName")}</span>
                                            <span className="details-box__value">{studentFullName}</span>
                                        </div>
                                        <div className="details-box__row">
                                            <span className="details-box__title">{t("managePayments.monthlyTuitionAmount")}</span>
                                            <span className="details-box__value">{formatCurrency(currency).format(summary?.amount || 0)}</span>
                                        </div>
                                        <div className="details-box__row">
                                            <span className="details-box__title">{t("managePayments.contractType")}</span>
                                            <span className="details-box__value">
                                                {`${summary?.contractType || t("managePayments.annual")} (${summary?.schedule || t("managePayments.monthly")})`}
                                            </span>
                                        </div>
                                    </div>
                                </Grid2>
                                <Grid2 size={{ xs: 12, md: 5 }}>
                                    <div className="details-box">
                                        <Typography variant="body1">
                                            {t("managePayments.contactHelpText")}
                                            <a href={`mailto:${t("managePayments.contactSupportEmail")}`}>{t("managePayments.contactSupportEmail")}</a>
                                        </Typography>
                                    </div>
                                </Grid2>
                            </Grid2>
                        </>
                    )}
                </Preloader>
            </div>
            <SnackbarToast
                open={!!errorSetPaymentPlan || !!errorConfigure}
                isError={!!errorSetPaymentPlan || !!errorConfigure}
                onClose={() => {
                    resetPaymentPlan();
                    resetConfigure();
                }}
            >
                {`${t("tuitionBilling.errorProceedMessage")} (status ${
                    (errorSetPaymentPlan || errorConfigure)?.response?.status
                })`}
            </SnackbarToast>
        </div>
    );
};
